// Token summoning at an aura's covered cells. Invasion pattern:
// "At the end of your turn, summon a Foot Soldier token to each
// affected enemy site with no units."
//
// Decoupled from SUMMON_TOKEN_AT_LOCATIONS (which uses scope expansions
// like 'adjacent' / 'nearby' relative to a source cell). Covered
// summon iterates the aura's own occupancy set, with optional filters
// on the underlying site's controller and presence of units.
//
// Pure picker + host-dispatched spawn. Host adapter contract:
//   findCatalogToken(name): cardRow | null
//   spawnTokenAt(cardRow, controllerRotated, point): void
//   getMeshes(): Iterable<[id, { userData, position }]>
//   getCellAt(): (x, z) => { col, row } | null
//   logAction(key, values?): void

import { anchorCoveredCells } from '../../core/auraPlacement';
import { countsAsUnit } from '../../core/typeline';
import { msg } from '../../../logMessage';
import { cellsInScope } from '../scope';

// Shared by the authoritative summon adapter and bot evaluation, so spell
// scoring counts the same sites that the effect will actually use.
export function collectSiteSummonCells(source, args, cards, controller, cols, rows) {
  const seat = c => c.cardId === 'sorcery-rubble' || c.ownerless ? null
    : c.controller ?? (c.rotated ? 'p2' : 'p1');
  const sites = cards.filter(c => c.zone === 'play' && c.isSite && c.cell);
  const anchor = args.location?.anchor === 'avatar'
    ? cards.find(c => c.zone === 'play' && c.type === 'Avatar' && seat(c) === controller) : source;
  const scope = args.location?.scope;
  const cells = scope === 'realm' ? null : cellsInScope(anchor?.cell, scope, cols, rows);
  const matches = (c, relation) => seat(c) != null && (relation === 'self' ? seat(c) === controller : seat(c) !== controller);
  return sites.filter(c => (!cells || cells.has(`${c.cell.col},${c.cell.row}`))
    && (!args.site_controller || matches(c, args.site_controller))
    && (!args.borders || sites.some(t => matches(t, args.borders)
      && Math.abs(t.cell.col - c.cell.col) + Math.abs(t.cell.row - c.cell.row) === 1)))
    .map(c => ({ ...c.cell }));
}

/**
 * Walk the source aura's covered cells and return every world-space
 * point eligible for token summoning under `args`.
 *
 * `args`:
 *   site_controller. 'enemy' / 'self' / undefined (no controller filter)
 *   require_empty. Default true; only cells with no unit qualify
 *
 * @returns {{ cellKey: string, point: { x: number, z: number } }[]}
 */
export function collectCoveredSummonCells(sourceCard, args, meshes, cellAt) {
  if (!sourceCard?._auraShape || !sourceCard?._auraAnchor) return [];
  const covered = new Set(anchorCoveredCells(sourceCard._auraAnchor, sourceCard._auraShape.shape));
  if (covered.size === 0) return [];

  const sourceRotated = !!sourceCard.rotated;
  const wantSiteController = args?.site_controller;
  const requireEmpty = args?.require_empty !== false;

  const sitesByCell = new Map();
  const unitsByCell = new Map();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    const key = `${cell.col},${cell.row}`;
    if (card.isSite) {
      sitesByCell.set(key, { card, position: mesh.position });
    } else if (countsAsUnit(card)) {
      // "with no units." Only UNITS (minions + avatars, codex) occupy a
      // cell for the emptiness gate. A lone unattached artifact or an aura
      // never blocks the summon.
      if (!unitsByCell.has(key)) unitsByCell.set(key, []);
      unitsByCell.get(key).push(card);
    }
  }

  const out = [];
  for (const key of covered) {
    const site = sitesByCell.get(key);
    if (!site) continue;
    if (wantSiteController === 'enemy' && !!site.card.rotated === sourceRotated) continue;
    if (wantSiteController === 'self'  && !!site.card.rotated !== sourceRotated) continue;
    if (requireEmpty && unitsByCell.has(key)) continue;
    out.push({ cellKey: key, point: { x: site.position.x, z: site.position.z } });
  }
  return out;
}

/**
 * Resolve `SUMMON_TOKEN_AT_COVERED`. Looks up the named token in the
 * catalog and spawns one copy at every eligible covered cell.
 *
 * @param {object} sourceCard
 * @param {{ tokenName?: string, site_controller?: 'enemy'|'self', require_empty?: boolean }} args
 * @param {object} host
 */
export function applySummonTokenAtCovered(sourceCard, args, host) {
  const tokenName = args?.tokenName;
  if (!tokenName) return;
  const tokenRow = host.findCatalogToken(tokenName);
  if (!tokenRow) {
    host.logAction('{source}: no "{token}" token in catalog', { source: sourceCard.name || msg('Source'), token: tokenName });
    return;
  }
  const cells = collectCoveredSummonCells(sourceCard, args, host.getMeshes(), host.getCellAt());
  if (cells.length === 0) {
    host.logAction('{source}: no eligible cells for {token}', { source: sourceCard.name || msg('Source'), token: tokenName });
    return;
  }
  const rotated = !!sourceCard.rotated;
  for (const { point } of cells) {
    host.spawnTokenAt(tokenRow, rotated, point);
  }
}
